import {DownloadMode} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {getDownloadMode, getProjectId, isDigestMode, isTvMode} from 'common/report/util';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';

import {apiClient} from './api-client';

export enum TourId {
  CustomizeGroups = `63c0a16d3e3e9a001618954d`,
  CustomizeGroupsLegacy = `63a3313d445d340016823dab`,
  DashboardUndoRedo = `635073fde899db0013d746fc`,
  DashboardUndoRedoNonMac = `63616fde2b369300164fe4a0`,
  Flows = `5eb5d96c17c5cf00047961aa`,
  Impact = `5e138855619c7e0004ea0cde`,
  InsightsNewQueryBuilder = `5d8182c31ce3d3000418a7a8`,
  LaunchAnalysis = `5e138855619c7e0004ea0cde`,
  LiveViewFeedback = `60d9e731c4e72c0016927aed`,
  NewMenuFeedback = `6102d91f47f077001aea7ce0`,
  Presence = `63601e77f55ce30014851e8a`,
  Retention = `5eb07cfa2e576200047f5eec`,
  SideQueryPanel = `618b58337c80d90019a81eaf`,
  TypecastInOverflow = `60b6ede0dac4fc001452e05b`,
  InsightsNewCountMenu = `6477e83d71bac040edba881e`,
  InsightsAnalysisMenu = `647fcfc452688000195b3526`,
}

export enum TourState {
  Completed = `completed`,
  Exited = `exited`,
  Started = `started`,
  NotStarted = `not-started`,
}

export enum EventName {
  TourCompleted = `Chameleon Completed Tour`,
  TourStepSeen = `Chameleon Step Seen`,
  TourExited = `Chameleon Exited Tour`,
  StartedTour = `Chameleon Started Tour`,
}

export function getTourState(tourId: TourId): TourState {
  const state = window.chmln?.data?.tours?.find((tour) => tour.get(`campaign.id`) === tourId)?.get(`state`);
  return state || TourState.NotStarted;
}

export function isChameleonAvailable() {
  return `chmln` in window;
}

export function showTour(tourId: TourId, showOnce = false) {
  window.chmln?.show(tourId, {once: showOnce});
}

let _chameleonReadyPromise: Promise<void> = null;

async function initChameleon(projectId) {
  /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
  if (_chameleonReadyPromise) {
    await _chameleonReadyPromise;
    return;
  }

  if (!ENABLE_WALKTHROUGHS || !projectId) {
    return;
  }

  // no walkthroughs while screenshotting / TV
  if (getDownloadMode() !== DownloadMode.None || isDigestMode() || isTvMode()) {
    return;
  }

  const gdprData = window.gdprData;
  await gdprData.waitUntilReady();
  if (!gdprData.allowSnippets || !gdprData.allowTracking || !userCookieUtils.getUserIdentityFromCookie()) {
    return;
  }

  // don't change, this snippet is copy-pasted from Chameleon docs
  // prettier-ignore
  // @ts-ignore
  // eslint-disable-next-line
  !function(t,n,o){var a="chmln",e="adminPreview",c="setup identify alias track clear set show on off custom help _data".split(" ");if(n[a]||(n[a]={}),n[a][e]&&(n[a][e]=!1),!n[a].root){n[a].accountToken=o,n[a].location=n.location.href.toString(),n[a].now=new Date;for(var s=0;s<c.length;s++)!function(){var t=n[a][c[s]+"_a"]=[];n[a][c[s]]=function(){t.push(arguments)}}();var i=t.createElement("script");i.src="https://fast.trychameleon.com/messo/"+o+"/messo.min.js",i.async=!0,t.head.appendChild(i)}}(document,window,"SrvPmJ9Xk1CL0GDm5uPAE3rqjeWBGsaHTadkB17tEACH7Y-1H46R2-BfdXaQMAr2AxVZ7d");

  const identity = await apiClient.get<any>(`/api/app/me/chameleon-identity`, {project_id: projectId});
  window.chmln?.identify(identity.id, identity);
  window.chmln?.on(`chmln:event`, (evName, props) => {
    // Only capture errors
    if (evName === `Chameleon Alert`) {
      reporter.error(`[Chameleon Alert] ${props.experience_name}`, {
        ...props,
        identity,
      });
    }
    window.dispatchEvent(new CustomEvent(`chameleonEvent`, {detail: {evName, props}}));
  });
  window.chmln?.on(`after:profile`, () => {
    window.dispatchEvent(new CustomEvent(`chameleonInitialized`));
  });
}

export function chameleonReadyPromise() {
  return _chameleonReadyPromise;
}

// Initialize chameleon in the common bundle so we can wait for it to be ready
_chameleonReadyPromise = initChameleon(getProjectId());
